import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchAdminAnalytics } from '../../services/authApi';
import { canonicalBuildPath } from '../../utils/buildRoutes';
import { Funnel, LineChart } from './Charts';
import './AdminAnalytics.css';

const PERIOD_PRESETS = [1, 7, 14, 30, 60, 90, 180, 365];
const PERIOD_MIN = 1;
const PERIOD_MAX = 400;

// Funnel / per-section chart colors: orange is "anonymous" and blue is
// "logged-in" everywhere on this page, so the two never swap meaning.
const ANON_COLOR = '#e0a96d';
const USER_COLOR = '#6db3e0';
const SINGLE_COLOR = '#8e7dd6';
const LIKE_COLOR = '#c48b9f';

// The merged activity chart draws seven series on one plot, which the four
// colors above cannot separate. It uses the shared categorical slots instead
// (see tokens.css): assigned in slot order, never reshuffled — the order is
// what keeps adjacent series apart under colorblind simulation.
const VIZ = ['var(--viz-1)', 'var(--viz-2)', 'var(--viz-3)', 'var(--viz-4)', 'var(--viz-5)', 'var(--viz-6)', 'var(--viz-7)', 'var(--viz-8)'];

// Page names the page-view beacon counts under (`pageSlug` in services/metrics.js),
// shown as the route they stand for: language-neutral, and it is what an admin
// would type to reach the page.
const PAGE_LABELS = {
    home: '/',
    gen: '/gen',
    gen_build: '/gen/<build>',
    share: '/s/<code>',
    encyclopedia: '/encyclopedia',
    my_builds: '/my-builds',
    community: '/community',
    community_creators: '/community/creators',
    community_creator: '/community/<creator>',
    profile: '/profile',
    chat: '/chat',
    ai_claim: '/ai/claim',
    terms: '/terms',
    other: '404',
};

const T = {
    title: { fr: 'Admin · Analytics', en: 'Admin · Analytics', de: 'Admin · Analytics', es: 'Admin · Analytics', pt: 'Admin · Analytics' },
    loading: { fr: 'Chargement des analytics...', en: 'Loading analytics...', de: 'Analytics werden geladen...', es: 'Cargando analytics...', pt: 'Carregando analytics...' },
    error: { fr: 'Impossible de charger les analytics.', en: 'Could not load analytics.', de: 'Analytics konnten nicht geladen werden.', es: 'No se han podido cargar las analytics.', pt: 'Não foi possível carregar as analytics.' },
    kpis: { fr: 'Indicateurs (période)', en: 'Period KPIs', de: 'Kennzahlen (Zeitraum)', es: 'KPIs del período', pt: 'KPIs do período' },
    visits: { fr: 'Visites', en: 'Visits', de: 'Besuche', es: 'Visitas', pt: 'Visitas' },
    gens: { fr: 'Générations', en: 'Generations', de: 'Generierungen', es: 'Generaciones', pt: 'Gerações' },
    gensPerVisit: { fr: 'Gens / visite', en: 'Gens / visit', de: 'Gens / Besuch', es: 'Gens / visita', pt: 'Gens / visita' },
    anonShare: { fr: 'Part anon.', en: 'Anon share', de: 'Anon-Anteil', es: 'Cuota anón.', pt: 'Parte anôn.' },
    savesPerGen: { fr: 'Saves / gen', en: 'Saves / gen', de: 'Saves / Gen', es: 'Saves / gen', pt: 'Saves / gen' },
    signupsPerVisit: { fr: 'Inscriptions / visite', en: 'Signups / visit', de: 'Signups / Besuch', es: 'Registros / visita', pt: 'Cadastros / visita' },
    vsPrev: { fr: 'vs période préc.', en: 'vs prior period', de: 'vs Vorperiode', es: 'vs período ant.', pt: 'vs período ant.' },
    botCeiling: { fr: 'Plafond anti-bot', en: 'Bot ceiling', de: 'Bot-Obergrenze', es: 'Techo anti-bot', pt: 'Teto anti-bot' },
    capHits: { fr: 'Hits gate', en: 'Gate hits', de: 'Gate-Hits', es: 'Hits del gate', pt: 'Hits do gate' },
    hitsPerAnon: { fr: 'Hits / gen anon.', en: 'Hits / anon gen', de: 'Hits / Anon-Gen', es: 'Hits / gen anón.', pt: 'Hits / gen anôn.' },
    generators: { fr: 'Utilisateurs (gens)', en: 'Users (gens)', de: 'Nutzer (Gens)', es: 'Usuarios (gens)', pt: 'Usuários (gens)' },
    activeGens: { fr: 'Actifs (période)', en: 'Active (period)', de: 'Aktiv (Zeitraum)', es: 'Activos (período)', pt: 'Ativos (período)' },
    oneAndDone: { fr: 'One-and-done', en: 'One-and-done', de: 'One-and-done', es: 'One-and-done', pt: 'One-and-done' },
    topGens: { fr: 'Top gens (ce mois)', en: 'Top gens (this month)', de: 'Top-Gens (dieser Monat)', es: 'Top gens (este mes)', pt: 'Top gens (este mês)' },
    withCounter: { fr: 'ayant généré', en: 'who have generated', de: 'mit Generierung', es: 'que han generado', pt: 'que geraram' },
    monthGens: { fr: 'Mois', en: 'Month', de: 'Monat', es: 'Mes', pt: 'Mês' },
    totalGens: { fr: 'Total', en: 'Total', de: 'Gesamt', es: 'Total', pt: 'Total' },
    chCapHits: { fr: 'Gate login_required / jour', en: 'login_required gate / day', de: 'login_required-Gate / Tag', es: 'Gate login_required / día', pt: 'Gate login_required / dia' },
    capDaySeries: { fr: 'Cap jour', en: 'Day cap', de: 'Tageslimit', es: 'Tope día', pt: 'Teto dia' },
    capWeekSeries: { fr: 'Cap semaine', en: 'Week cap', de: 'Wochenlimit', es: 'Tope semana', pt: 'Teto semana' },
    guestConversion: { fr: 'Invités · Conversion', en: 'Guests · Conversion', de: 'Gäste · Konversion', es: 'Invitados · Conversión', pt: 'Convidados · Conversão' },
    gateShown: { fr: 'Popups affichés', en: 'Gates shown', de: 'Gates gezeigt', es: 'Gates mostrados', pt: 'Gates exibidos' },
    gateLoginClicks: { fr: 'Clics Discord', en: 'Discord clicks', de: 'Discord-Klicks', es: 'Clics Discord', pt: 'Cliques Discord' },
    gateAuth: { fr: 'Connexions abouties', en: 'Logins completed', de: 'Logins abgeschlossen', es: 'Inicios completados', pt: 'Logins concluídos' },
    gateSignups: { fr: 'Nouveaux comptes', en: 'New accounts', de: 'Neue Konten', es: 'Cuentas nuevas', pt: 'Contas novas' },
    gateStayed: { fr: 'Restés sur le site', en: 'Stayed on site', de: 'Blieben auf der Seite', es: 'Se quedaron', pt: 'Ficaram no site' },
    gateAbandoned: { fr: 'Partis', en: 'Walked away', de: 'Abgesprungen', es: 'Se fueron', pt: 'Foram embora' },
    chCapGate: { fr: 'Issues du gate / jour', en: 'Gate outcomes / day', de: 'Gate-Ausgänge / Tag', es: 'Resultados del gate / día', pt: 'Desfechos do gate / dia' },
    gateShownSeries: { fr: 'Affiché', en: 'Shown', de: 'Gezeigt', es: 'Mostrado', pt: 'Exibido' },
    gateLoginSeries: { fr: 'Clic Discord', en: 'Discord click', de: 'Discord-Klick', es: 'Clic Discord', pt: 'Clique Discord' },
    gateStayedSeries: { fr: 'Resté', en: 'Stayed', de: 'Geblieben', es: 'Se quedó', pt: 'Ficou' },
    gateAuthSeries: { fr: 'Connecté', en: 'Logged in', de: 'Angemeldet', es: 'Conectado', pt: 'Conectado' },
    gateNote: {
        fr: 'Une session capée = un gate affiché. « Restés » et « Partis » se partagent ceux qui n’ont pas cliqué Discord.',
        en: 'One gate shown per capped session. "Stayed" and "Walked away" split those who did not click Discord.',
        de: 'Ein Gate pro gedrosselter Sitzung. „Geblieben“ und „Abgesprungen“ teilen sich alle ohne Discord-Klick.',
        es: 'Un gate por sesión limitada. «Se quedaron» y «Se fueron» reparten a quienes no hicieron clic en Discord.',
        pt: 'Um gate por sessão limitada. "Ficaram" e "Foram embora" dividem quem não clicou em Discord.',
    },
    capNote: {
        fr: 'Plafond serveur de 50 gens/jour par IP : l’atteindre signale de l’automatisation, pas de l’enthousiasme. Chaque requête bloquée compte.',
        en: 'Server ceiling of 50 gens/day per IP: hitting it signals automation, not enthusiasm. Every blocked request counts.',
        de: 'Server-Limit von 50 Gens/Tag pro IP: Erreichen heißt Automatisierung, nicht Begeisterung. Jede blockierte Anfrage zählt.',
        es: 'Techo de servidor de 50 gens/día por IP: alcanzarlo indica automatización, no entusiasmo. Cuenta cada petición bloqueada.',
        pt: 'Teto de servidor de 50 gens/dia por IP: atingi-lo indica automação, não entusiasmo. Conta cada requisição bloqueada.',
    },
    pulse: { fr: 'Aujourd\'hui', en: 'Today', de: 'Heute', es: 'Hoy', pt: 'Hoje' },
    loggedIn: { fr: 'Connectés', en: 'Logged-in', de: 'Angemeldet', es: 'Conectados', pt: 'Conectados' },
    anon: { fr: 'Anonymes', en: 'Anonymous', de: 'Anonym', es: 'Anónimos', pt: 'Anônimos' },
    today: { fr: "Aujourd'hui", en: 'Today', de: 'Heute', es: 'Hoy', pt: 'Hoje' },
    week: { fr: 'Cette semaine', en: 'This week', de: 'Diese Woche', es: 'Esta semana', pt: 'Esta semana' },
    month: { fr: 'Ce mois', en: 'This month', de: 'Dieser Monat', es: 'Este mes', pt: 'Este mês' },
    jobs: { fr: 'Ops · Solveur', en: 'Ops · Solver', de: 'Ops · Solver', es: 'Ops · Solver', pt: 'Ops · Solver' },
    ops: { fr: 'Ops', en: 'Ops', de: 'Ops', es: 'Ops', pt: 'Ops' },
    errors: { fr: 'Erreurs', en: 'Errors', de: 'Fehler', es: 'Errores', pt: 'Erros' },
    errClient: { fr: 'Navigateur', en: 'Browser', de: 'Browser', es: 'Navegador', pt: 'Navegador' },
    errServer: { fr: 'Serveur', en: 'Server', de: 'Server', es: 'Servidor', pt: 'Servidor' },
    chErrors: { fr: 'Erreurs par jour', en: 'Errors per day', de: 'Fehler pro Tag', es: 'Errores por día', pt: 'Erros por dia' },
    errNote: {
        fr: "Comptes bruts. Le journal ci-dessous ne garde qu'une ligne par erreur distincte.",
        en: 'Raw counts. The activity log below keeps one row per distinct error.',
        de: 'Rohwerte. Das Protokoll unten behält eine Zeile je eindeutigem Fehler.',
        es: 'Conteos brutos. El registro de abajo guarda una fila por error distinto.',
        pt: 'Contagens brutas. O registro abaixo guarda uma linha por erro distinto.',
    },
    fallbacks: { fr: 'Générations passées par le serveur', en: 'Generations sent to the server', de: 'Generierungen über den Server', es: 'Generaciones enviadas al servidor', pt: 'Gerações enviadas ao servidor' },
    fallbackNote: {
        fr: 'Pourquoi le navigateur a refusé de résoudre. Orange = routage normal, rouge = vraie panne.',
        en: 'Why the browser declined to solve. Amber = routine routing, red = a real failure.',
        de: 'Warum der Browser nicht gelöst hat. Orange = normales Routing, Rot = echter Fehler.',
        es: 'Por qué el navegador no resolvió. Ámbar = enrutamiento normal, rojo = fallo real.',
        pt: 'Por que o navegador não resolveu. Âmbar = roteamento normal, vermelho = falha real.',
    },
    periodGroup: { fr: 'Période', en: 'Period', de: 'Zeitraum', es: 'Periodo', pt: 'Período' },
    queued: { fr: 'En file', en: 'Queued', de: 'In Warteschlange', es: 'En cola', pt: 'Na fila' },
    running: { fr: 'En cours', en: 'Running', de: 'Läuft', es: 'En curso', pt: 'Em curso' },
    done24: { fr: 'Terminés (24h)', en: 'Done (24h)', de: 'Fertig (24 Std.)', es: 'Terminados (24 h)', pt: 'Concluídos (24 h)' },
    err24: { fr: 'Erreurs (24h)', en: 'Errors (24h)', de: 'Fehler (24 Std.)', es: 'Errores (24 h)', pt: 'Erros (24 h)' },
    stopped24: { fr: 'Stoppés (24h)', en: 'Stopped (24h)', de: 'Gestoppt (24 Std.)', es: 'Detenidos (24 h)', pt: 'Parados (24 h)' },
    errRate: { fr: 'Taux erreur (24h)', en: 'Error rate (24h)', de: 'Fehlerrate (24 Std.)', es: 'Tasa error (24 h)', pt: 'Taxa erro (24 h)' },
    oldestQueued: { fr: 'Plus vieux en file', en: 'Oldest queued', de: 'Älteste Warteschlange', es: 'Más antiguo en cola', pt: 'Mais antigo na fila' },
    oldestRunning: { fr: 'Plus vieux running', en: 'Oldest running', de: 'Älteste Running', es: 'Más antiguo running', pt: 'Mais antigo running' },
    staleQueued: { fr: 'Stale queued', en: 'Stale queued', de: 'Stale queued', es: 'Stale queued', pt: 'Stale queued' },
    staleRunning: { fr: 'Stale running', en: 'Stale running', de: 'Stale running', es: 'Stale running', pt: 'Stale running' },
    p50Solve: { fr: 'p50 solve (24h)', en: 'p50 solve (24h)', de: 'p50 Solve (24 Std.)', es: 'p50 solve (24 h)', pt: 'p50 solve (24 h)' },
    ai: { fr: 'AI chat', en: 'AI chat', de: 'AI-Chat', es: 'Chat IA', pt: 'Chat IA' },
    aiInterpret: { fr: 'Interprets', en: 'Interprets', de: 'Interprets', es: 'Interprets', pt: 'Interprets' },
    aiClaims: { fr: 'Claims créés', en: 'Claims created', de: 'Claims erstellt', es: 'Claims creados', pt: 'Claims criados' },
    aiConsumed: { fr: 'Claims consommés', en: 'Claims consumed', de: 'Claims verbraucht', es: 'Claims consumidos', pt: 'Claims consumidos' },
    aiConsumeRate: { fr: 'Taux claim', en: 'Claim rate', de: 'Claim-Rate', es: 'Tasa claim', pt: 'Taxa claim' },
    chAi: { fr: 'AI / jour', en: 'AI / day', de: 'AI / Tag', es: 'AI / día', pt: 'AI / dia' },
    aiInterpretSeries: { fr: 'Interpret', en: 'Interpret', de: 'Interpret', es: 'Interpret', pt: 'Interpret' },
    aiClaimCreatedSeries: { fr: 'Claim créé', en: 'Claim created', de: 'Claim erstellt', es: 'Claim creado', pt: 'Claim criado' },
    aiClaimConsumedSeries: { fr: 'Claim consommé', en: 'Claim consumed', de: 'Claim verbraucht', es: 'Claim consumido', pt: 'Claim consumido' },
    anonAuthors: { fr: 'Auteurs anonymes', en: 'Anonymous authors', de: 'Anonyme Autoren', es: 'Autores anónimos', pt: 'Autores anônimos' },
    signupsSeries: { fr: 'Inscriptions', en: 'Signups', de: 'Registrierungen', es: 'Registros', pt: 'Cadastros' },
    anonAuthorsNote: {
        fr: 'Un invité qui sauvegarde un stuff crée un profil anonyme : compté à part, ce n’est pas une inscription.',
        en: 'A guest who saves a build mints an anonymous profile — counted separately, it is not a signup.',
        de: 'Ein Gast, der einen Build speichert, erzeugt ein anonymes Profil — separat gezählt, keine Registrierung.',
        es: 'Un invitado que guarda una build crea un perfil anónimo: se cuenta aparte, no es un registro.',
        pt: 'Um convidado que salva uma build cria um perfil anônimo: contado à parte, não é um cadastro.',
    },
    // The one activity chart. It replaced a grid of six per-metric charts, whose
    // titles are gone with them; its legend carries seven names on one row, so
    // those are short — the card title already says "per day". Likes/day is
    // deliberately not among them: too flat to be worth a seventh line.
    chActivity: { fr: 'Activité / jour', en: 'Activity / day', de: 'Aktivität / Tag', es: 'Actividad / día', pt: 'Atividade / dia' },
    sGenUser: { fr: 'Générations (connectés)', en: 'Generations (logged-in)', de: 'Generierungen (angemeldet)', es: 'Generaciones (conectados)', pt: 'Gerações (conectados)' },
    sGenAnon: { fr: 'Générations (invités)', en: 'Generations (guests)', de: 'Generierungen (Gäste)', es: 'Generaciones (invitados)', pt: 'Gerações (convidados)' },
    sBuilds: { fr: 'Stuffs sauvegardés', en: 'Builds saved', de: 'Gespeicherte Builds', es: 'Builds guardadas', pt: 'Builds salvas' },
    sUniqueVisitors: { fr: 'Visiteurs uniques', en: 'Unique visitors', de: 'Eindeutige Besucher', es: 'Visitantes únicos', pt: 'Visitantes únicos' },
    sViews: { fr: 'Vues communauté', en: 'Community views', de: 'Community-Aufrufe', es: 'Visitas comunidad', pt: 'Visualizações comunidade' },
    // Crawler traffic, kept out of the activity chart above (see backend
    // accounts/bots.py): its own card, so a crawl cannot flatten the real series.
    chBots: { fr: 'Bots / jour', en: 'Bots / day', de: 'Bots / Tag', es: 'Bots / día', pt: 'Bots / dia' },
    topBuilds: { fr: 'Top stuffs (par vues)', en: 'Top builds (by views)', de: 'Top-Builds (nach Aufrufen)', es: 'Top builds (por visitas)', pt: 'Top builds (por visualizações)' },
    builds: { fr: 'Stuffs', en: 'Builds', de: 'Builds', es: 'Builds', pt: 'Builds' },
    users: { fr: 'Utilisateurs', en: 'Users', de: 'Benutzer', es: 'Usuarios', pt: 'Usuários' },
    likes: { fr: 'Likes', en: 'Likes', de: 'Likes', es: 'Likes', pt: 'Curtidas' },
    views: { fr: 'Vues', en: 'Views', de: 'Aufrufe', es: 'Visitas', pt: 'Visualizações' },
    unmappedSkins: { fr: 'Skins non mappés', en: 'Unmapped skins', de: 'Nicht gemappte Skins', es: 'Skins sin mapear', pt: 'Skins sem mapa' },
    unmappedDb: { fr: 'Hats/capes sans skin', en: 'Hats/capes without skin', de: 'Hüte/Umhänge ohne Skin', es: 'Sombreros/capas sin skin', pt: 'Chapéus/capas sem skin' },
    unmappedHits: { fr: 'Hits de rendu', en: 'Render hits', de: 'Render-Treffer', es: 'Hits de render', pt: 'Hits de render' },
    unmappedItem: { fr: 'Item', en: 'Item', de: 'Item', es: 'Ítem', pt: 'Item' },
    unmappedType: { fr: 'Type', en: 'Type', de: 'Typ', es: 'Tipo', pt: 'Tipo' },
    unmappedHitsCol: { fr: 'Hits', en: 'Hits', de: 'Treffer', es: 'Hits', pt: 'Hits' },
    unmappedLast: { fr: 'Dernier', en: 'Last seen', de: 'Zuletzt', es: 'Último', pt: 'Último' },
    unmappedOk: { fr: 'Tous les hats/capes ont un render_skin_id.', en: 'All hats/capes have a render_skin_id.', de: 'Alle Hüte/Umhänge haben eine render_skin_id.', es: 'Todos los sombreros/capas tienen render_skin_id.', pt: 'Todos os chapéus/capas têm render_skin_id.' },
    noData: { fr: 'Aucune donnée pour le moment.', en: 'No data yet.', de: 'Noch keine Daten.', es: 'Aún no hay datos.', pt: 'Ainda não há dados.' },
    days: { fr: 'j', en: 'd', de: 'T', es: 'd', pt: 'd' },
    customDays: { fr: 'Perso', en: 'Custom', de: 'Custom', es: 'Custom', pt: 'Custom' },
    anonPartial: {
        fr: 'Gens anon. incomplets au-delà de ~{n} j (rétention buckets).',
        en: 'Anon gens incomplete beyond ~{n}d (bucket retention).',
        de: 'Anon-Gens unvollständig jenseits von ~{n} T (Bucket-Retention).',
        es: 'Gens anón. incompletos más allá de ~{n} d (retención).',
        pt: 'Gens anôn. incompletos além de ~{n} d (retenção).',
    },
    pageViews: { fr: 'Pages vues / jour', en: 'Page views / day', de: 'Seitenaufrufe / Tag', es: 'Páginas vistas / día', pt: 'Páginas vistas / dia' },
    pageViewsDate: { fr: 'Jour', en: 'Day', de: 'Tag', es: 'Día', pt: 'Dia' },
    pageViewsTotal: { fr: 'Total', en: 'Total', de: 'Gesamt', es: 'Total', pt: 'Total' },
    pageViewsNote: {
        fr: 'Un chargement de page ou un changement de route vers une autre page. Changer d’onglet ou de build dans le générateur ne compte pas.',
        en: 'A page load, or a route change to a different page. Switching tab or build inside the generator does not count.',
        de: 'Ein Seitenaufruf oder Routenwechsel zu einer anderen Seite. Tab- oder Build-Wechsel im Generator zählen nicht.',
        es: 'Una carga de página o un cambio de ruta a otra página. Cambiar de pestaña o de build en el generador no cuenta.',
        pt: 'Um carregamento de página ou mudança de rota para outra página. Trocar de aba ou de build no gerador não conta.',
    },
    totals: { fr: 'Totaux', en: 'Lifetime', de: 'Gesamt', es: 'Totales', pt: 'Totais' },
};

const tr = (key, lang) => (T[key] && (T[key][lang] || T[key].en)) || key;
const fmt = (n) => new Intl.NumberFormat().format(Number(n || 0));

/** Total one key across a daily-series array (the charts are already fetched,
 *  so a period total needs no second request). */
const sumKey = (rows, key) =>
    (Array.isArray(rows) ? rows : []).reduce((acc, row) => acc + Number(row?.[key] || 0), 0);

/** Share of a total as a percentage, floored at a visible sliver so a reason
 *  that fired once is still a bar rather than nothing. */
const pctOf = (n, total) => (total > 0 ? Math.max(2, Math.round((Number(n || 0) / total) * 100)) : 0);

function clampDays(n) {
    const v = Math.round(Number(n));
    if (!Number.isFinite(v)) return 30;
    return Math.max(PERIOD_MIN, Math.min(PERIOD_MAX, v));
}

function fmtRatio(n, { pct = false } = {}) {
    if (n == null || Number.isNaN(Number(n))) return '—';
    const v = Number(n);
    if (pct) return `${(v * 100).toFixed(v * 100 >= 10 ? 0 : 1)}%`;
    return v >= 10 ? v.toFixed(1) : v.toFixed(2);
}

function fmtSecs(n) {
    if (n == null || Number.isNaN(Number(n))) return '—';
    const v = Number(n);
    if (v < 60) return `${Math.round(v)}s`;
    if (v < 3600) return `${(v / 60).toFixed(v >= 600 ? 0 : 1)}m`;
    if (v < 86400) return `${(v / 3600).toFixed(v >= 36000 ? 0 : 1)}h`;
    return `${(v / 86400).toFixed(v >= 864000 ? 0 : 1)}d`;
}

function fmtDelta(n, { pct = false } = {}) {
    if (n == null || Number.isNaN(Number(n))) return { text: null, tone: null };
    const v = Number(n);
    const sign = v > 0 ? '+' : '';
    const tone = v > 0 ? 'up' : v < 0 ? 'down' : null;
    if (pct) return { text: `${sign}${(v * 100).toFixed(1)}pp`, tone };
    if (Math.abs(v) >= 10) return { text: `${sign}${fmt(Math.round(v))}`, tone };
    return { text: `${sign}${v.toFixed(2)}`, tone };
}

function StatCard({ label, value, hint, sub, delta, deltaTone }) {
    const deltaCls = deltaTone === 'up' ? ' up' : deltaTone === 'down' ? ' down' : '';
    return (
        <div className="admin-card">
            <span className="admin-card-label">{label}</span>
            <span className="admin-card-value">
                {value}
                {hint ? <span className="admin-card-hint">{hint}</span> : null}
            </span>
            {delta != null ? <span className={`admin-card-delta${deltaCls}`}>{delta}</span> : null}
            {sub ? <span className="admin-card-sub">{sub}</span> : null}
        </div>
    );
}

/** Period KPI card with vs-prior delta. */
function KpiCard({ label, value, hint, deltaRaw, pct, vsPrev }) {
    const d = fmtDelta(deltaRaw, { pct });
    return (
        <StatCard
            label={label}
            value={value}
            hint={hint}
            delta={d.text}
            deltaTone={d.tone}
            sub={vsPrev}
        />
    );
}

function ChartCard({ title, children }) {
    return (
        <div className="admin-chart-card">
            <span className="admin-chart-title">{title}</span>
            {children}
        </div>
    );
}

/**
 * Page views per page per day: a chart of the busiest pages (the palette has
 * seven slots, and the legend filters them) above the exact table, one row per
 * day, newest first, one column per page in order of traffic.
 */
function PageViews({ pages, lang }) {
    if (!pages?.length) return <p className="admin-empty">{tr('noData', lang)}</p>;
    const label = (page) => PAGE_LABELS[page] || page;
    const days = pages[0].daily.map((d) => d.date).reverse();
    const byPage = pages.map((p) => Object.fromEntries(p.daily.map((d) => [d.date, d.count])));
    return (
        <>
            <LineChart
                height={260}
                xTicks={7}
                ariaLabel={tr('pageViews', lang)}
                series={pages.slice(0, VIZ.length).map((p, i) => toSeries(p.daily, 'count', label(p.page), VIZ[i]))}
            />
            <div className="admin-pageviews-scroll">
                <table className="admin-table admin-pageviews-table">
                    <thead>
                        <tr>
                            <th>{tr('pageViewsDate', lang)}</th>
                            {pages.map((p) => <th key={p.page}>{label(p.page)}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        <tr className="admin-pageviews-total">
                            <td>{tr('pageViewsTotal', lang)}</td>
                            {pages.map((p) => <td key={p.page}>{fmt(p.total)}</td>)}
                        </tr>
                        {days.map((date) => (
                            <tr key={date}>
                                <td>{date}</td>
                                {pages.map((p, i) => <td key={p.page}>{fmt(byPage[i][date])}</td>)}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}

function toSeries(rows, key, name, color) {
    return { name, color, points: (rows || []).map((d) => ({ date: d.date, value: d[key] })) };
}

function AdminAnalytics({ lang }) {
    const [data, setData] = useState(null);
    const [status, setStatus] = useState('loading');
    const [days, setDays] = useState(30);
    const [customDraft, setCustomDraft] = useState('');

    const load = useCallback((period) => {
        let cancelled = false;
        setStatus((prev) => (prev === 'ready' ? 'ready' : 'loading'));
        fetchAdminAnalytics(period)
            .then((payload) => {
                if (cancelled) return;
                setData(payload);
                setStatus('ready');
            })
            .catch((err) => {
                if (cancelled) return;
                console.warn('fetchAdminAnalytics failed:', err);
                setStatus('error');
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => load(days), [days, load]);

    const applyCustom = () => {
        const next = clampDays(customDraft);
        setCustomDraft(String(next));
        setDays(next);
    };

    const presets = data?.period?.presets?.length ? data.period.presets : PERIOD_PRESETS;
    const charts = data?.charts;
    const kpis = data?.kpis;
    const delta = data?.kpis_delta;
    const pulse = data?.pulse;
    const vsPrev = tr('vsPrev', lang);
    const anonNote = data?.period?.anon_series_partial
        ? (tr('anonPartial', lang) || '').replace('{n}', String(data.period.anon_retention_days || 40))
        : null;

    return (
        <section className="admin-analytics">
            <div className="admin-header">
                <h2 className="admin-section-title">{tr('title', lang)}</h2>
                <div className="admin-period-controls">
                    <div className="admin-period-toggle" role="group" aria-label={tr('periodGroup', lang)}>
                        {presets.map((p) => (
                            <button
                                key={p}
                                type="button"
                                className={`admin-period-btn${p === days ? ' active' : ''}`}
                                onClick={() => {
                                    setDays(p);
                                    setCustomDraft('');
                                }}
                            >
                                {p}{tr('days', lang)}
                            </button>
                        ))}
                    </div>
                    <form
                        className="admin-period-custom"
                        onSubmit={(e) => {
                            e.preventDefault();
                            applyCustom();
                        }}
                    >
                        <label className="admin-period-custom-label" htmlFor="admin-period-custom-input">
                            {tr('customDays', lang)}
                        </label>
                        <input
                            id="admin-period-custom-input"
                            className="admin-period-custom-input"
                            type="number"
                            min={PERIOD_MIN}
                            max={PERIOD_MAX}
                            inputMode="numeric"
                            placeholder={String(days)}
                            value={customDraft}
                            onChange={(e) => setCustomDraft(e.target.value)}
                            onBlur={() => {
                                if (customDraft !== '') applyCustom();
                            }}
                        />
                        <span className="admin-period-custom-unit">{tr('days', lang)}</span>
                    </form>
                </div>
            </div>
            {anonNote ? <p className="admin-period-note">{anonNote}</p> : null}

            {status === 'loading' && !data && <p className="admin-empty">{tr('loading', lang)}</p>}
            {status === 'error' && <p className="admin-empty">{tr('error', lang)}</p>}

            {data && (
                <>
                    <h3 className="admin-subtitle">{tr('kpis', lang)}</h3>
                    <div className="admin-grid">
                        <KpiCard
                            label={tr('visits', lang)}
                            value={fmt(kpis?.visits)}
                            deltaRaw={delta?.visits}
                            vsPrev={vsPrev}
                        />
                        <KpiCard
                            label={tr('gens', lang)}
                            value={fmt(kpis?.gens_total)}
                            hint={`${fmtRatio(kpis?.anon_share, { pct: true })} ${tr('anon', lang).toLowerCase()}`}
                            deltaRaw={delta?.gens_total}
                            vsPrev={vsPrev}
                        />
                        <KpiCard
                            label={tr('gensPerVisit', lang)}
                            value={fmtRatio(kpis?.gens_per_visit)}
                            deltaRaw={delta?.gens_per_visit}
                            vsPrev={vsPrev}
                        />
                        <KpiCard
                            label={tr('anonShare', lang)}
                            value={fmtRatio(kpis?.anon_share, { pct: true })}
                            deltaRaw={delta?.anon_share}
                            pct
                            vsPrev={vsPrev}
                        />
                        <KpiCard
                            label={tr('savesPerGen', lang)}
                            value={fmtRatio(kpis?.saves_per_gen)}
                            deltaRaw={delta?.saves_per_gen}
                            vsPrev={vsPrev}
                        />
                        <KpiCard
                            label={tr('signupsPerVisit', lang)}
                            value={fmtRatio(kpis?.signups_per_visit)}
                            deltaRaw={delta?.signups_per_visit}
                            vsPrev={vsPrev}
                        />
                    </div>

                    {charts && (
                        <ChartCard title={tr('chActivity', lang)}>
                            {/* One plot for the whole daily story, full width, rather than six
                                cards a third of the screen wide: these series are read against
                                each other (do saves follow generations? do visits convert?),
                                which side-by-side axes make impossible. They share a unit, so
                                one axis is honest; the legend filters away the magnitudes that
                                would otherwise flatten the small series. */}
                            <LineChart
                                height={320}
                                xTicks={7}
                                ariaLabel={tr('chActivity', lang)}
                                series={[
                                    toSeries(charts.generations, 'user', tr('sGenUser', lang), VIZ[0]),
                                    toSeries(charts.generations, 'anon', tr('sGenAnon', lang), VIZ[1]),
                                    toSeries(charts.visits, 'count', tr('visits', lang), VIZ[2]),
                                    toSeries(charts.signups, 'signups', tr('signupsSeries', lang), VIZ[3]),
                                    toSeries(charts.signups, 'anon_authors', tr('anonAuthors', lang), VIZ[4]),
                                    toSeries(charts.builds, 'count', tr('sBuilds', lang), VIZ[5]),
                                    toSeries(charts.community_views, 'count', tr('sViews', lang), VIZ[6]),
                                    toSeries(charts.unique_visitors, 'count', tr('sUniqueVisitors', lang), VIZ[7]),
                                ]}
                            />
                        </ChartCard>
                    )}

                    {charts?.bots && (
                        <ChartCard title={tr('chBots', lang)}>
                            {/* Same slots as their human counterparts above, so the
                                colour already says which line is which. */}
                            <LineChart
                                xTicks={7}
                                ariaLabel={tr('chBots', lang)}
                                series={[
                                    toSeries(charts.bots, 'visits', tr('visits', lang), VIZ[2]),
                                    toSeries(charts.bots, 'community_views', tr('sViews', lang), VIZ[6]),
                                ]}
                            />
                        </ChartCard>
                    )}

                    <h3 className="admin-subtitle">{tr('pageViews', lang)}</h3>
                    <p className="admin-period-note">{tr('pageViewsNote', lang)}</p>
                    <ChartCard title={tr('pageViews', lang)}>
                        <PageViews pages={data.page_views} lang={lang} />
                    </ChartCard>

                    <h3 className="admin-subtitle">{tr('guestConversion', lang)}</h3>
                    <Funnel
                        stages={[
                            { name: tr('gateShown', lang), value: data.freemium?.gate_shown, color: ANON_COLOR },
                            { name: tr('gateLoginClicks', lang), value: data.freemium?.gate_login_clicks, color: USER_COLOR },
                            { name: tr('gateAuth', lang), value: data.freemium?.gate_auth, color: SINGLE_COLOR },
                            { name: tr('gateSignups', lang), value: data.freemium?.gate_signups, color: LIKE_COLOR },
                        ]}
                    />
                    <div className="admin-grid">
                        <KpiCard
                            label={tr('gateStayed', lang)}
                            value={fmt(data.freemium?.gate_stayed)}
                            deltaRaw={data.freemium_delta?.gate_stayed}
                            vsPrev={vsPrev}
                        />
                        <KpiCard
                            label={tr('gateAbandoned', lang)}
                            value={fmt(data.freemium?.gate_abandoned)}
                            hint={fmtRatio(data.freemium?.gate_abandon_rate, { pct: true })}
                            deltaRaw={data.freemium_delta?.gate_abandoned}
                            vsPrev={vsPrev}
                        />
                    </div>
                    <p className="admin-period-note">{tr('gateNote', lang)}</p>
                    {charts?.cap_gate ? (
                        <div className="admin-charts-grid">
                            <ChartCard title={tr('chCapGate', lang)}>
                                <LineChart
                                    series={[
                                        toSeries(charts.cap_gate, 'shown', tr('gateShownSeries', lang), ANON_COLOR),
                                        toSeries(charts.cap_gate, 'login', tr('gateLoginSeries', lang), USER_COLOR),
                                        toSeries(charts.cap_gate, 'stayed', tr('gateStayedSeries', lang), SINGLE_COLOR),
                                        toSeries(charts.cap_gate, 'auth', tr('gateAuthSeries', lang), LIKE_COLOR),
                                    ]}
                                />
                            </ChartCard>
                        </div>
                    ) : null}

                    <h3 className="admin-subtitle">{tr('generators', lang)}</h3>
                    <div className="admin-grid">
                        <StatCard
                            label={tr('activeGens', lang)}
                            value={fmt(data.generators?.active_in_period)}
                            sub={`${fmt(data.generators?.with_counter)} ${tr('withCounter', lang)}`}
                        />
                        <StatCard
                            label={tr('oneAndDone', lang)}
                            value={fmt(data.generators?.one_and_done)}
                            hint={fmtRatio(data.generators?.one_and_done_share, { pct: true })}
                        />
                    </div>
                    {data.generators?.top?.length > 0 ? (
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>{tr('topGens', lang)}</th>
                                    <th>{tr('monthGens', lang)}</th>
                                    <th>{tr('totalGens', lang)}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.generators.top.map((g) => (
                                    <tr key={`gen-${g.owner_slug || g.owner}`}>
                                        <td>
                                            {g.owner_slug ? (
                                                <Link
                                                    className="admin-build-link"
                                                    to={`/${lang}/community/${encodeURIComponent(g.owner_slug)}`}
                                                >
                                                    {g.owner}
                                                </Link>
                                            ) : (
                                                g.owner
                                            )}
                                        </td>
                                        <td>{fmt(g.month_count)}</td>
                                        <td>{fmt(g.total_count)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : null}

                    <h3 className="admin-subtitle">{tr('ai', lang)}</h3>
                    <div className="admin-grid">
                        <KpiCard
                            label={tr('aiInterpret', lang)}
                            value={fmt(data.ai?.interprets)}
                            deltaRaw={data.ai_delta?.interprets}
                            vsPrev={vsPrev}
                        />
                        <KpiCard
                            label={tr('aiClaims', lang)}
                            value={fmt(data.ai?.claims_created)}
                            deltaRaw={data.ai_delta?.claims_created}
                            vsPrev={vsPrev}
                        />
                        <KpiCard
                            label={tr('aiConsumed', lang)}
                            value={fmt(data.ai?.claims_consumed)}
                            deltaRaw={data.ai_delta?.claims_consumed}
                            vsPrev={vsPrev}
                        />
                        <KpiCard
                            label={tr('aiConsumeRate', lang)}
                            value={fmtRatio(data.ai?.claim_consume_rate, { pct: true })}
                            deltaRaw={data.ai_delta?.claim_consume_rate}
                            pct
                            vsPrev={vsPrev}
                        />
                    </div>
                    {charts?.ai ? (
                        <div className="admin-charts-grid">
                            <ChartCard title={tr('chAi', lang)}>
                                <LineChart
                                    series={[
                                        toSeries(charts.ai, 'interpret', tr('aiInterpretSeries', lang), USER_COLOR),
                                        toSeries(charts.ai, 'claim_created', tr('aiClaimCreatedSeries', lang), ANON_COLOR),
                                        toSeries(charts.ai, 'claim_consumed', tr('aiClaimConsumedSeries', lang), LIKE_COLOR),
                                    ]}
                                />
                            </ChartCard>
                        </div>
                    ) : null}

                    <h3 className="admin-subtitle">{tr('pulse', lang)}</h3>
                    <div className="admin-grid">
                        <StatCard
                            label={`${tr('gens', lang)} · ${tr('loggedIn', lang)}`}
                            value={fmt(pulse?.gens_user_today)}
                            hint={tr('today', lang)}
                            sub={`${tr('month', lang)} ${fmt(pulse?.gens_user_month)}`}
                        />
                        <StatCard
                            label={`${tr('gens', lang)} · ${tr('anon', lang)}`}
                            value={fmt(pulse?.gens_anon_today)}
                            hint={tr('today', lang)}
                            sub={`${tr('week', lang)} ${fmt(pulse?.gens_anon_week)}`}
                        />
                    </div>

                    <h3 className="admin-subtitle">{tr('totals', lang)}</h3>
                    <div className="admin-grid">
                        <StatCard label={tr('users', lang)} value={fmt(data.totals?.users)} />
                        <StatCard label={tr('anonAuthors', lang)} value={fmt(data.totals?.anon_authors)} />
                        <StatCard label={tr('builds', lang)} value={fmt(data.totals?.builds)} />
                    </div>
                    <p className="admin-period-note">{tr('anonAuthorsNote', lang)}</p>

                    <h3 className="admin-subtitle">{tr('topBuilds', lang)}</h3>
                    {data.top_builds && data.top_builds.length > 0 ? (
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>{tr('builds', lang)}</th>
                                    <th>{tr('users', lang)}</th>
                                    <th>{tr('views', lang)}</th>
                                    <th>{tr('likes', lang)}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.top_builds.map((b) => (
                                    <tr key={b.public_code + b.owner_slug}>
                                        <td>
                                            {b.owner_slug && b.public_code ? (
                                                <Link
                                                    className="admin-build-link"
                                                    to={canonicalBuildPath(lang, b.owner_slug, b.public_code)}
                                                >
                                                    {b.name}
                                                </Link>
                                            ) : (
                                                b.name
                                            )}
                                        </td>
                                        <td>{b.owner}</td>
                                        <td>{fmt(b.views)}</td>
                                        <td>{fmt(b.likes)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <p className="admin-empty">{tr('noData', lang)}</p>
                    )}

                    <h3 className="admin-subtitle">{tr('ops', lang)}</h3>
                    <h3 className="admin-subtitle admin-subtitle-nested">{tr('botCeiling', lang)}</h3>
                    <div className="admin-grid">
                        <KpiCard
                            label={tr('capHits', lang)}
                            value={fmt(data.freemium?.cap_hits)}
                            deltaRaw={data.freemium_delta?.cap_hits}
                            vsPrev={vsPrev}
                        />
                        <KpiCard
                            label={tr('hitsPerAnon', lang)}
                            value={fmtRatio(data.freemium?.hits_per_anon_gen)}
                            deltaRaw={data.freemium_delta?.hits_per_anon_gen}
                            vsPrev={vsPrev}
                        />
                    </div>
                    <p className="admin-period-note">{tr('capNote', lang)}</p>
                    {charts?.cap_hits ? (
                        <div className="admin-charts-grid">
                            <ChartCard title={tr('chCapHits', lang)}>
                                <LineChart
                                    series={[
                                        toSeries(charts.cap_hits, 'day', tr('capDaySeries', lang), ANON_COLOR),
                                        toSeries(charts.cap_hits, 'week', tr('capWeekSeries', lang), LIKE_COLOR),
                                    ]}
                                />
                            </ChartCard>
                        </div>
                    ) : null}

                    <h3 className="admin-subtitle admin-subtitle-nested">{tr('errors', lang)}</h3>
                    <div className="admin-grid">
                        <KpiCard
                            label={tr('errClient', lang)}
                            value={fmt(sumKey(charts?.errors, 'client'))}
                        />
                        <KpiCard
                            label={tr('errServer', lang)}
                            value={fmt(sumKey(charts?.errors, 'server'))}
                        />
                    </div>
                    <p className="admin-period-note">{tr('errNote', lang)}</p>
                    {charts?.errors ? (
                        <div className="admin-charts-grid">
                            <ChartCard title={tr('chErrors', lang)}>
                                <LineChart
                                    series={[
                                        toSeries(charts.errors, 'client', tr('errClient', lang), ANON_COLOR),
                                        toSeries(charts.errors, 'server', tr('errServer', lang), LIKE_COLOR),
                                    ]}
                                />
                            </ChartCard>
                        </div>
                    ) : null}

                    <h3 className="admin-subtitle admin-subtitle-nested">{tr('fallbacks', lang)}</h3>
                    <p className="admin-period-note">{tr('fallbackNote', lang)}</p>
                    {data.fallbacks?.reasons?.length ? (
                        <ul className="admin-reason-list">
                            {data.fallbacks.reasons.map((row) => (
                                <li key={row.slug} className="admin-reason-row">
                                    <span className={`admin-reason-dot ${row.routine ? 'routine' : 'defect'}`} aria-hidden="true" />
                                    <span className="admin-reason-label">{row.label}</span>
                                    <span className="admin-reason-bar">
                                        <span
                                            className={`admin-reason-fill ${row.routine ? 'routine' : 'defect'}`}
                                            style={{ width: `${pctOf(row.count, data.fallbacks.total)}%` }}
                                        />
                                    </span>
                                    <span className="admin-reason-count">{fmt(row.count)}</span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="admin-empty">{tr('noData', lang)}</p>
                    )}

                    <h3 className="admin-subtitle admin-subtitle-nested">{tr('jobs', lang)}</h3>
                    <div className="admin-grid">
                        <StatCard label={tr('queued', lang)} value={fmt(data.jobs?.queued)} />
                        <StatCard label={tr('running', lang)} value={fmt(data.jobs?.running)} />
                        <StatCard
                            label={tr('oldestQueued', lang)}
                            value={fmtSecs(data.jobs?.oldest_queued_age_s)}
                        />
                        <StatCard
                            label={tr('oldestRunning', lang)}
                            value={fmtSecs(data.jobs?.oldest_running_age_s)}
                        />
                        <StatCard label={tr('staleQueued', lang)} value={fmt(data.jobs?.stale_queued)} />
                        <StatCard label={tr('staleRunning', lang)} value={fmt(data.jobs?.stale_running)} />
                        <StatCard label={tr('done24', lang)} value={fmt(data.jobs?.done_24h)} />
                        <StatCard label={tr('err24', lang)} value={fmt(data.jobs?.error_24h)} />
                        <StatCard label={tr('stopped24', lang)} value={fmt(data.jobs?.stopped_24h)} />
                        <StatCard
                            label={tr('errRate', lang)}
                            value={fmtRatio(data.jobs?.error_rate_24h, { pct: true })}
                        />
                        <StatCard
                            label={tr('p50Solve', lang)}
                            value={fmtSecs(data.jobs?.p50_solve_s)}
                        />
                    </div>

                    <h3 className="admin-subtitle admin-subtitle-nested">{tr('unmappedSkins', lang)}</h3>
                    <div className="admin-grid">
                        <StatCard label={tr('unmappedDb', lang)} value={fmt(data.unmapped_skins?.db_count)} />
                        <StatCard label={tr('unmappedHits', lang)} value={fmt(data.unmapped_skins?.hit_total)} />
                    </div>
                    {data.unmapped_skins?.db_count > 0 && data.unmapped_skins?.sample?.length > 0 ? (
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>id</th>
                                    <th>{tr('unmappedItem', lang)}</th>
                                    <th>{tr('unmappedType', lang)}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.unmapped_skins.sample.map((row) => (
                                    <tr key={`um-${row.item_id}`}>
                                        <td>{row.item_id}</td>
                                        <td>{row.name || '—'}</td>
                                        <td>{row.type || row.type_id || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <p className="admin-empty">{tr('unmappedOk', lang)}</p>
                    )}
                    {data.unmapped_skins?.hits?.length > 0 ? (
                        <>
                            <h3 className="admin-subtitle admin-subtitle-nested">{tr('unmappedHits', lang)}</h3>
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>id</th>
                                        <th>{tr('unmappedItem', lang)}</th>
                                        <th>{tr('unmappedHitsCol', lang)}</th>
                                        <th>{tr('unmappedLast', lang)}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.unmapped_skins.hits.map((row) => (
                                        <tr key={`hit-${row.item_id}`}>
                                            <td>{row.item_id}</td>
                                            <td>{row.name || '—'}</td>
                                            <td>{fmt(row.hit_count)}</td>
                                            <td>{row.last_seen_at ? String(row.last_seen_at).slice(0, 16).replace('T', ' ') : '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    ) : null}
                </>
            )}
        </section>
    );
}

export default AdminAnalytics;
